import React, { useEffect, useState, createContext, useContext } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import type { Settings, Page, Project } from '../../types';
import LiveThemeEditor from '../../components/LiveThemeEditor';
import ProjectEditModal from '../../components/ProjectEditModal';

// Project Editor Context for Live Editing
interface ProjectEditorContextType {
    openProjectEditor: (project?: Project) => void;
}
const ProjectEditorContext = createContext<ProjectEditorContextType | null>(null);
export const useProjectEditor = () => useContext(ProjectEditorContext);

type EditorState = { mode: 'edit', project: Project } | { mode: 'new' } | null;


const TwitterIcon: React.FC<{className?: string}> = ({className}) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path></svg>);
const FacebookIcon: React.FC<{className?: string}> = ({className}) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>);
const LinkedInIcon: React.FC<{className?: string}> = ({className}) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>);
const InstagramIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg>;
const LinkIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.72" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.72" /></svg>;


export const SocialIcon: React.FC<{ platform: string, className?: string }> = ({ platform, className }) => {
    const lowerCasePlatform = platform.toLowerCase();
    if (lowerCasePlatform.includes('twitter')) return <TwitterIcon className={className} />;
    if (lowerCasePlatform.includes('facebook')) return <FacebookIcon className={className} />;
    if (lowerCasePlatform.includes('linkedin')) return <LinkedInIcon className={className} />;
    if (lowerCasePlatform.includes('instagram')) return <InstagramIcon className={className} />;
    return <LinkIcon className={className} />;
};

const HamburgerIcon: React.FC<{ open: boolean, color: string }> = ({ open, color }) => (
    <div className="w-6 h-5 flex flex-col justify-between items-center relative">
        <span 
            className={`w-full h-0.5 transition-all duration-300 ${open ? 'rotate-45 translate-y-[9px]' : ''}`} 
            style={{ backgroundColor: color }}
        />
        <span 
            className={`w-full h-0.5 transition-all duration-300 ${open ? 'opacity-0 scale-0' : ''}`} 
            style={{ backgroundColor: color }}
        />
        <span 
            className={`w-full h-0.5 transition-all duration-300 ${open ? '-rotate-45 -translate-y-[9px]' : ''}`} 
            style={{ backgroundColor: color }}
        />
    </div>
);


const Header: React.FC<{ settings: Settings, pages: Page[], hideMainLogo: boolean }> = ({ settings, pages, hideMainLogo }) => {
    const [scrolled, setScrolled] = useState(false);
    const [headerVisible, setHeaderVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            setScrolled(currentScrollY > 10);
            if (currentScrollY > lastScrollY && currentScrollY > 100) {
                // Scrolling down
                setHeaderVisible(false);
            } else {
                // Scrolling up
                setHeaderVisible(true);
            }
            setLastScrollY(currentScrollY);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location]);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);


    const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
        `relative whitespace-nowrap text-sm uppercase tracking-widest transition-colors ${
            isActive 
                ? 'font-bold' 
                : 'hover:text-[var(--color-nav-link-hover)]'
        } after:content-[''] after:absolute after:left-0 after:bottom-[-2px] after:w-full after:h-[1px] after:bg-white after:scale-x-0 after:origin-left after:transition-transform group-hover:after:scale-x-100 ${
            isActive ? 'after:scale-x-100' : ''
        }`;
        
    const navLinkStyle = ({ isActive }: { isActive: boolean }) => ({
        color: isActive 
            ? (settings.theme?.frontendTheme?.activeLinkColor || '#FFFFFF') 
            : (settings.theme?.frontendTheme?.navLinkColor || '#9CA3AF')
    });

    // Filter out generic pages that are now handled by specific components
    const staticPageSlugs = ['about-us', 'contact-us', 'our-services'];
    const publishedPages = pages.filter(p => p.published && !staticPageSlugs.includes(p.slug));

    const hamburgerColor = mobileMenuOpen 
        ? (settings.theme?.frontendTheme?.activeLinkColor || '#FFFFFF') 
        : scrolled 
        ? (settings.theme?.frontendTheme?.navLinkColor || '#9CA3AF') 
        : '#FFFFFF';

    return (
        <>
            <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out font-header ${scrolled || mobileMenuOpen ? 'bg-header-bg shadow-md p-6' : 'bg-transparent p-8'} ${headerVisible || mobileMenuOpen ? 'translate-y-0' : '-translate-y-full'}`}>
                <div className="container mx-auto flex justify-between items-center">
                    <Link to="/" className="text-xl font-bold text-white tracking-wider flex items-center z-50">
                        {!hideMainLogo && (
                            settings.logoUrl ? (
                                <img 
                                    src={settings.logoUrl} 
                                    alt={settings.companyName} 
                                    style={{ width: `${settings.logoWidth || 120}px`, height: 'auto' }}
                                    className="object-contain" 
                                />
                            ) : (
                                settings.companyName
                            )
                        )}
                    </Link>
                    
                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center space-x-8 group">
                        <NavLink to="/" className={navLinkClasses} style={navLinkStyle} end>Portfolio</NavLink>
                        <NavLink to="/about" className={navLinkClasses} style={navLinkStyle}>About</NavLink>
                        <NavLink to="/services" className={navLinkClasses} style={navLinkStyle}>Services</NavLink>
                        <NavLink to="/team" className={navLinkClasses} style={navLinkStyle}>Our Team</NavLink>
                        <NavLink to="/associates" className={navLinkClasses} style={navLinkStyle}>Associates</NavLink>
                        {settings.enableJournal && (
                            <NavLink to="/journal" className={navLinkClasses} style={navLinkStyle}>Journal</NavLink>
                        )}
                         <NavLink to="/videos" className={navLinkClasses} style={navLinkStyle}>Videos</NavLink>
                         {publishedPages.map(page => (
                            <NavLink key={page.id} to={`/page/${page.slug}`} className={navLinkClasses} style={navLinkStyle}>{page.title}</NavLink>
                        ))}
                        <NavLink to="/contact" className={navLinkClasses} style={navLinkStyle}>Contact</NavLink>
                    </nav>

                    {/* Mobile Menu Button */}
                    <button 
                        onClick={() => setMobileMenuOpen(prev => !prev)} 
                        className="md:hidden focus:outline-none z-50 p-2"
                        aria-label="Toggle Menu"
                    >
                        <HamburgerIcon open={mobileMenuOpen} color={hamburgerColor} />
                    </button>
                </div>
            </header>

            {/* Mobile Navigation Drawer */}
            <div 
                className={`fixed inset-0 z-40 flex flex-col items-center justify-center transition-all duration-300 ease-in-out md:hidden ${
                    mobileMenuOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-10 pointer-events-none'
                }`}
                style={{ 
                    backgroundColor: settings.theme?.frontendTheme?.headerBackgroundColor || '#111111'
                }}
            >
                <nav className="flex flex-col items-center space-y-6 text-center">
                    <NavLink to="/" className={navLinkClasses} style={navLinkStyle} end onClick={() => setMobileMenuOpen(false)}>Portfolio</NavLink>
                    <NavLink to="/about" className={navLinkClasses} style={navLinkStyle} onClick={() => setMobileMenuOpen(false)}>About</NavLink>
                    <NavLink to="/services" className={navLinkClasses} style={navLinkStyle} onClick={() => setMobileMenuOpen(false)}>Services</NavLink>
                    <NavLink to="/team" className={navLinkClasses} style={navLinkStyle} onClick={() => setMobileMenuOpen(false)}>Our Team</NavLink>
                    <NavLink to="/associates" className={navLinkClasses} style={navLinkStyle} onClick={() => setMobileMenuOpen(false)}>Associates</NavLink>
                    {settings.enableJournal && (
                        <NavLink to="/journal" className={navLinkClasses} style={navLinkStyle} onClick={() => setMobileMenuOpen(false)}>Journal</NavLink>
                    )}
                    <NavLink to="/videos" className={navLinkClasses} style={navLinkStyle} onClick={() => setMobileMenuOpen(false)}>Videos</NavLink>
                    {publishedPages.map(page => (
                        <NavLink key={page.id} to={`/page/${page.slug}`} className={navLinkClasses} style={navLinkStyle} onClick={() => setMobileMenuOpen(false)}>{page.title}</NavLink>
                    ))}
                    <NavLink to="/contact" className={navLinkClasses} style={navLinkStyle} onClick={() => setMobileMenuOpen(false)}>Contact</NavLink>
                </nav>
            </div>
        </>
    );
};

const Footer: React.FC<{ settings: Settings }> = ({ settings }) => (
    <footer className="bg-footer-bg text-gray-600 py-10 px-4 border-t border-gray-200 font-body">
        <div className="container mx-auto text-center text-sm">
             {settings.socialLinks && settings.socialLinks.length > 0 && (
                <div className="flex justify-center items-center space-x-6 mb-6 flex-wrap gap-y-4">
                    {settings.socialLinks.map((link, index) => (
                        <a 
                            key={index} 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            title={link.platform}
                            className="text-gray-500 hover:text-brand-dark transition-colors"
                        >
                            <SocialIcon platform={link.platform} className="w-6 h-6" />
                        </a>
                    ))}
                </div>
            )}
            <p>{settings.footerContent}</p>
        </div>
    </footer>
);

import { updateMetaTag, updateOgTag, updateFavicon } from '../../hooks/useSEO';
export { updateMetaTag, updateOgTag, updateFavicon };



interface PublicLayoutProps {
    settings: Settings;
    pages: Page[];
    children: React.ReactNode;
    updateSettings: (newSettings: Settings) => void;
    onProjectUpdate: () => void;
}

const PublicLayout: React.FC<PublicLayoutProps> = ({ settings, pages, children, updateSettings, onProjectUpdate }) => {
    const location = useLocation();
    const [isDevMode, setIsDevMode] = useState(false);
    const [editorState, setEditorState] = useState<EditorState>(null);

    const isAssociatePage = location.pathname.startsWith('/associates/');
    
    useEffect(() => {
        setIsDevMode(sessionStorage.getItem('devMode') === 'true');
    }, [location]);

    const openProjectEditor = (project?: Project) => {
        if (project) {
            setEditorState({ mode: 'edit', project });
        } else {
            setEditorState({ mode: 'new' });
        }
    };

    const closeProjectEditor = () => {
        setEditorState(null);
    };

    useEffect(() => {
        if (settings) {
            // Update SEO and Meta
            document.title = settings.seo.title;
            updateMetaTag('description', settings.seo.description);
            updateFavicon(settings.seo.favicon);
            updateOgTag('title', settings.openGraph.title);
            updateOgTag('description', settings.openGraph.description);
            updateOgTag('image', settings.openGraph.imageUrl);

            // Update Theme
            if (settings.theme.frontendTheme) {
                const feTheme = settings.theme.frontendTheme;
                const fonts = feTheme.fontFamily;
                const fontFamilies = Object.values(fonts).filter((v, i, a) => a.indexOf(v) === i); // Unique fonts
                // FIX: Explicitly type `f` as `string` to resolve the error "Property 'replace' does not exist on type 'unknown'".
                const fontUrl = `https://fonts.googleapis.com/css2?${fontFamilies.map((f: string) => `family=${f.replace(/ /g, '+')}:wght@400;700`).join('&')}&display=swap`;
                
                const fontLinkId = 'dynamic-font-link';
                let fontLinkElement = document.getElementById(fontLinkId) as HTMLLinkElement;
                if (!fontLinkElement) {
                    fontLinkElement = document.createElement('link');
                    fontLinkElement.id = fontLinkId;
                    fontLinkElement.rel = 'stylesheet';
                    document.head.appendChild(fontLinkElement);
                }
                if (fontLinkElement.href !== fontUrl) {
                    fontLinkElement.href = fontUrl;
                }

                const styleId = 'dynamic-theme-styles';
                let styleElement = document.getElementById(styleId);
                if (!styleElement) {
                    styleElement = document.createElement('style');
                    styleElement.id = styleId;
                    document.head.appendChild(styleElement);
                }
                styleElement.innerHTML = `
                    :root {
                        --color-primary: ${feTheme.primaryColor};
                        --color-secondary: ${feTheme.secondaryColor};
                        --color-header-bg: ${feTheme.headerBackgroundColor};
                        --color-footer-bg: ${feTheme.footerBackgroundColor};
                        --color-nav-link: ${feTheme.navLinkColor};
                        --color-nav-link-hover: ${feTheme.navLinkHoverColor};
                        --font-family-header: '${fonts.header}';
                        --font-family-heading: '${fonts.heading}';
                        --font-family-body: '${fonts.body}';
                    }
                    body { background-color: ${feTheme.pageBackgroundColor}; }
                `;
            }
        }
    }, [settings]);

    return (
        <ProjectEditorContext.Provider value={{ openProjectEditor }}>
            <div className="flex flex-col min-h-screen text-brand-dark font-body">
                <Header settings={settings} pages={pages} hideMainLogo={isAssociatePage} />
                <main key={location.pathname} className="flex-grow animate-page-fade-in">{children}</main>
                <Footer settings={settings} />
                {isDevMode && <LiveThemeEditor settings={settings} onSave={updateSettings} />}
                {isDevMode && editorState && (
                    <ProjectEditModal
                        mode={editorState.mode}
                        project={editorState.mode === 'edit' ? editorState.project : undefined}
                        settings={settings}
                        onClose={closeProjectEditor}
                        onSaved={() => {
                            onProjectUpdate();
                            closeProjectEditor();
                        }}
                    />
                )}
                <style>{`
                    @keyframes page-fade-in {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .animate-page-fade-in {
                        animation: page-fade-in 0.5s ease-out forwards;
                    }
                `}</style>
            </div>
        </ProjectEditorContext.Provider>
    );
};

export default PublicLayout;
